import { GTMPush } from '@utilities/gtm';
import { sha256 } from '@utilities/sha-256';
import md5 from 'md5';

import { SubscriptionStoreAddress, SubscriptionStoreUser } from '@/store/subscription/store.types';

import {
    BuyerInfo,
    ObjectSubscription,
    ObjectSubscriptionProps,
    SubscriptionEndpointModel,
} from './endpoint-types';

const API_ENDPOINT = '/umbraco/api/ajaxform/submitfooterform';

const subscriptionBaseApiCall = async ({
    subscriptionInfo,
    userInfo,
    address,
    buyerInfo,
    fcToken,
}: {
    subscriptionInfo: ObjectSubscription;
    userInfo: SubscriptionStoreUser;
    address?: SubscriptionStoreAddress;
    buyerInfo?: BuyerInfo;
    fcToken: string;
}) => {
    const body: SubscriptionEndpointModel = {
        FirstName: userInfo.firstName || '',
        LastName: userInfo.lastName,
        LastNamePrefix: userInfo.lastNamePrefix || '',
        Email: userInfo.email,
        Website: userInfo.website || '',
        ProjectId: subscriptionInfo.projectPhaseId || '',
        PropertyId: subscriptionInfo.propertyTypeId || '',
        AreaId: subscriptionInfo.areaId || '',
        DistrictId: subscriptionInfo.districtId || '',
        ConstructionNumberId: subscriptionInfo.constructionNumberId || '',
        FcToken: fcToken,
    };

    if (userInfo.gender) body.IsMale = userInfo.gender === 'Mr';

    if (address) {
        body.Phone = address.phoneNumber;
        body.PostalCode = address.zipcode || '';
        body.Street = address.street || '';
        body.HouseNumber = address.houseNumber;
        body.HouseNumberAddition = address.houseNumberAddition || '';
        body.City = address.city;
        body.Country = address.country;
    }

    if (buyerInfo) {
        body.IntendedUsage = buyerInfo.intendedUsage;
        body.PurchaseTimeframe = buyerInfo.purchaseTimeframe;
        body.FinancialCheck = buyerInfo.financialCheck;
        body.Comment = buyerInfo.comment;
    }

    const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    const succeeded = await response.json();

    if (!response.ok || succeeded !== true) {
        throw new Error(`Newsletter subscription failed with status ${response.status}.`);
    }
};

export const subscribeToObject = async (props: ObjectSubscriptionProps) => {
    await subscriptionBaseApiCall(props);

    const { userInfo, subscriptionInfo, GTMEventLabel } = props;
    if (!GTMEventLabel) return;

    let eventAction: string;
    let eventLabel = GTMEventLabel;

    switch (props.subscriptionInfo.type) {
        case 'area':
        case 'district':
            eventAction = 'Subscribe Gebied';
            break;
        case 'project':
            eventAction = 'Subscribe Gebied';
            break;
        case 'propertyType':
            eventAction = 'Subscribe Woningtype';
            eventLabel = `${subscriptionInfo.propertyTypeId} ${GTMEventLabel}`;
            break;
        case 'constructionNumber':
            eventAction = 'Subscribe Bouwnummer';
            eventLabel = `${subscriptionInfo.constructionNumberId} ${GTMEventLabel}`;
            break;
    }

    GTMPush({
        event: 'gtm.SubscriptionSubscription',
        eventCategory: 'Subscription',
        eventAction,
        eventLabel,
        email: md5(userInfo.email),
        email_sha_256: await sha256(userInfo.email),
    });
};
